<template>
  <div class="user-select-clauses">
    <CCard class="border-0 shadow-sm shell">
      <CCardBody class="p-4">
        <div class="page-header">
          <div>
            <div class="kicker">{{ text.kicker }}</div>
            <h2>{{ text.title }}</h2>
            <p>{{ text.subtitle }}</p>
          </div>
        </div>

        <div class="actions">
          <CButton color="secondary" variant="outline" class="mr-2" @click="goBack">{{ text.back }}</CButton>
          <CButton color="primary" @click="saveAndContinue">{{ text.continue }}</CButton>
        </div>

        <div v-if="groupedClauses.length" class="category-filter-bar">
          <button
            v-for="item in categoryFilters"
            :key="item.key"
            type="button"
            class="category-filter-chip"
            :class="{ 'category-filter-chip--active': activeCategory === item.key }"
            @click="activeCategory = item.key"
          >
            {{ item.name }}
            <span class="category-filter-chip__count">{{ item.count }}</span>
          </button>
        </div>

        <div v-if="filteredGroups.length" class="category-stack">
          <section v-for="group in filteredGroups" :key="group.key" class="category-section">
            <div class="category-section__header">
              <div>
                <div class="category-section__title">{{ group.name }}</div>
                <div class="category-section__meta">{{ clauseCountLabel(group.items.length) }}</div>
              </div>
            </div>

            <div class="clause-list">
              <label v-for="item in group.items" :key="item.id" class="clause-option">
                <input type="checkbox" :value="item.id" v-model="selectedClauses">
                <div>
                  <div class="clause-title">{{ item.title }}</div>
                  <div class="clause-meta">{{ item.category }} · {{ item.sourceType === 'default' ? text.starter : text.myClause }}</div>
                  <div class="clause-copy">{{ item.content }}</div>
                </div>
              </label>
            </div>
          </section>
        </div>

        <div v-else class="empty-state">
          {{ text.empty }}
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import Service from '@/service/api'
import { localizeClauseRecord } from '@/utils/clause-localization'

const PAGE_TEXT = {
  en: {
    kicker: 'Agreement Clauses',
    title: 'Select Clauses',
    subtitle: 'Choose the clauses you want to include before submitting the agreement to admin.',
    back: 'Back',
    continue: 'Continue',
    starter: 'Starter',
    myClause: 'My Clause',
    empty: 'No clauses are available for this agreement yet.',
    general: 'General',
    allCategories: 'All Categories',
    clauseSingular: 'clause',
    clausePlural: 'clauses'
  },
  th: {
    kicker: 'ข้อกำหนดข้อตกลง',
    title: 'เลือกข้อกำหนด',
    subtitle: 'เลือกข้อกำหนดที่ต้องการก่อนส่งข้อตกลงให้ผู้ดูแลตรวจสอบ',
    back: 'ย้อนกลับ',
    continue: 'ดำเนินการต่อ',
    starter: 'ข้อกำหนดเริ่มต้น',
    myClause: 'ข้อกำหนดของฉัน',
    empty: 'ยังไม่มีข้อกำหนดสำหรับข้อตกลงนี้',
    general: 'ทั่วไป',
    allCategories: 'ทุกหมวดหมู่',
    clauseSingular: 'ข้อกำหนด',
    clausePlural: 'ข้อกำหนด'
  }
}

function normalizeClauseId(item) {
  return item && (item.id || item._id) ? String(item.id || item._id) : ''
}

export default {
  name: 'UserSelectClauses',
  data() {
    return {
      clauses: [],
      selectedClauses: [],
      activeCategory: 'all'
    }
  },
  computed: {
    localeCode() {
      return String(this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'en').toLowerCase().startsWith('th') ? 'th' : 'en'
    },
    text() {
      return PAGE_TEXT[this.localeCode]
    },
    localizedClauses() {
      return this.clauses.map(item => localizeClauseRecord(item, this.localeCode))
    },
    agreementId() {
      return this.$route.params.id
    },
    groupedClauses() {
      const groups = this.localizedClauses.reduce((collection, item) => {
        const name = String(item.category || this.text.general).trim() || this.text.general
        const key = name.toLowerCase()
        if (!collection[key]) {
          collection[key] = { key, name, items: [] }
        }
        collection[key].items.push(item)
        return collection
      }, {})

      return Object.values(groups)
        .sort((left, right) => left.name.localeCompare(right.name))
        .map(group => ({
          ...group,
          items: group.items.slice().sort((left, right) => String(left.title || '').localeCompare(String(right.title || '')))
        }))
    },
    categoryFilters() {
      return [
        { key: 'all', name: this.text.allCategories, count: this.localizedClauses.length }
      ].concat(this.groupedClauses.map(group => ({
        key: group.key,
        name: group.name,
        count: group.items.length
      })))
    },
    filteredGroups() {
      if (this.activeCategory === 'all') return this.groupedClauses
      return this.groupedClauses.filter(group => group.key === this.activeCategory)
    }
  },
  mounted() {
    this.loadClauses()
  },
  watch: {
    localeCode() {
      this.activeCategory = 'all'
    }
  },
  methods: {
    clauseCountLabel(count) {
      const amount = Number(count || 0)
      const label = amount === 1 ? this.text.clauseSingular : this.text.clausePlural
      return `${amount} ${label}`
    },
    async loadClauses() {
      const response = await Service.agreements('clauses', { id: this.agreementId })
      const data = response && response.data && response.data.data ? response.data.data : {}
      this.clauses = Array.isArray(data.clauses)
        ? data.clauses.map(item => ({
            ...item,
            id: normalizeClauseId(item)
          })).filter(item => item.id)
        : []
      this.selectedClauses = Array.isArray(data.selectedClauses)
        ? data.selectedClauses.map(item => normalizeClauseId(item) || String(item || '')).filter(Boolean)
        : []
      if (!this.categoryFilters.find(item => item.key === this.activeCategory)) {
        this.activeCategory = 'all'
      }
    },
    goBack() {
      const type = String(this.$route.query.type || 'Exchange Student').toLowerCase()
      if (type === 'double degree') return this.$router.push(`/dashboard/user-agreements/${this.agreementId}/double-degree`)
      if (type === 'extension agreement') return this.$router.push(`/dashboard/user-agreements/${this.agreementId}/extension`)
      if (type === 'mou file') return this.$router.push(`/dashboard/user-agreements/${this.agreementId}/mou`)
      this.$router.push(`/dashboard/user-agreements/${this.agreementId}/exchange`)
    },
    async saveAndContinue() {
      await Service.agreements('update-clauses', {
        id: this.agreementId,
        selectedClauses: this.selectedClauses
      })
      this.$router.push(`/dashboard/user-agreements/${this.agreementId}/review-summary`)
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("user-select-clauses");

.shell { border-radius: var(--app-radius-xl); }
.page-header { margin-bottom: 20px; }
.kicker { color: #8c1515; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.page-header h2 { color: #344767; margin: 6px 0; }
.page-header p { color: #718096; }
.actions { display: flex; justify-content: flex-end; margin-bottom: 18px; }
.category-filter-bar { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 18px; }
.category-filter-chip {
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #52606d;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 8px;
  min-height: 40px;
  padding: 8px 14px;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
}
.category-filter-chip--active {
  background: #8c1515;
  border-color: #8c1515;
  box-shadow: 0 10px 20px rgba(140, 21, 21, 0.16);
  color: #fff;
}
.category-filter-chip__count {
  background: rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  font-size: 0.78rem;
  line-height: 1;
  padding: 4px 7px;
}
.category-stack { display: grid; gap: 18px; }
.category-section { display: grid; gap: 12px; }
.category-section__header { align-items: center; display: flex; justify-content: space-between; }
.category-section__title { color: #344767; font-size: 1rem; font-weight: 700; }
.category-section__meta { color: #718096; font-size: 0.88rem; margin-top: 2px; }
.clause-list { display: grid; gap: 14px; }
.clause-option { align-items: flex-start; border: 1px solid var(--app-border); border-radius: var(--app-radius-md); cursor: pointer; display: grid; gap: 12px; grid-template-columns: auto 1fr; padding: 16px; }
.clause-title { color: #344767; font-weight: 700; }
.clause-meta, .clause-copy { color: #718096; }
.clause-copy { margin-top: 6px; }
.empty-state { color: #718096; padding: 20px 0 8px; text-align: center; }

@media (max-width: 768px) {
  .category-filter-bar { gap: 8px; }
  .category-filter-chip { width: 100%; justify-content: space-between; }
}
</style>
